import React, {useContext, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import FormField, {Props} from '@src/components/Form/FormField';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderPaymentModel} from '@src/@interfaces/models/Order/IOrderPaymentModel';
import {TableCaption} from './TableCaption';
import {PaymentTypeField} from './PaymentTypeField';
import {AmountField} from './AmountField';
import {IsChargeBackField} from './IsChargeBackField';
import {DatetimeAtField} from './DatetimeAtField';
import {stringifyDate} from '@src/helpers/dates';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import TableRow from '@views/orders/fields/payments/TableRow';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.NUMBER,
        invisible: true,
        forceVisible: true,
    },
    {
        name: 'payment_type_id',
        label: 'OrderForm.payments.fields.payment_type',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'is_charge_back',
        label: 'OrderForm.payments.fields.is_charge_back',
        type: ListConfigFieldTypes.SWITCH,
    },
    {
        name: 'amount',
        label: 'OrderForm.payments.fields.amount',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'datetime_at',
        label: 'OrderForm.payments.fields.datetime_at',
        type: ListConfigFieldTypes.TIME_SINCE,
    },
];

const columnsTDProps: Record<keyof IOrderPaymentModel, React.HTMLAttributes<HTMLTableDataCellElement>> = {
    id: {className: 'flex-grow-0'},
    payment_type_id: {style: {minWidth: '100px'}},
    amount: {style: {minWidth: '100px'}},
    datetime_at: {style: {minWidth: '100px'}},
};

function getCustomColumnComponent(config: ListConfigNamedColumn) {
    switch (config.name) {
        case 'id':
            return null;

        case 'payment_type_id':
            return PaymentTypeField;

        case 'is_charge_back':
            return IsChargeBackField;

        case 'amount':
            return AmountField;

        case 'datetime_at':
            return DatetimeAtField;
    }

    return null;
}

function ActualForm(props: Props<IOrderModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control, setValue} = useFormContext<IOrderModel>();
    const canDelete = usePermissionGranted('orders@can-delete-saved-payments');
    const {fields, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'payments',
        keyName: 'autoGeneratedUuid',
    });

    const {tabName, modelOptions} = props;

    useEffect(() => void setValue('payments_is_empty', fields.length <= 0 ? 1 : null), [fields.length, setValue]);

    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length)
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const isDisabled = designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const listContentProps: ControlledListProps<IOrderPaymentModel>['listContentProps'] = {
            tableCaptionNode: <TableCaption/>,
            tableHeadProps: {
                additionalControlNode: <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        disabled={isDisabled}
                        onClick={() => append({
                            payment_type_id: null,
                            is_charge_back: false,
                            amount: 0,
                            datetime_at: stringifyDate(),
                        })}
                >
                    <i className="fa fa-plus"/>
                </button>,
            },
            tableBodyProps: {
                getTDProps(column): React.HTMLAttributes<HTMLTableDataCellElement> {
                    return columnsTDProps[column.name] ?? {};
                },

                renderControls(record, index) {
                    if (record?.id && !canDelete) {
                        return null;
                    }

                    if (record?.id) {
                        return <ConfirmableButton
                                type="button"
                                size="sm"
                                color="danger"
                                disabled={isDisabled}
                                onClick={() => remove(index)}>
                            <i className="fa fa-times"/>
                        </ConfirmableButton>;
                    }

                    return <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            disabled={isDisabled}
                            onClick={() => remove(index)}>
                        <i className="fa fa-times"/>
                    </button>;
                },

                renderRow(properties, baseRender, baseContentRenderer) {
                    return <TableRow
                            properties={properties}
                            baseRenderer={baseRender}
                            baseContentRenderer={baseContentRenderer}
                            modelOptions={modelOptions}
                    />;
                },
            }
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IOrderModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [
        isDisabled,
        canDelete,
        control,
        props.fieldName,
        props.formName,
        props.tabName,
        props.model,
        props.modelOptions,
        props.config,
        props.forceReadonly,
        props.forceDisabled,
        append,
        remove,
        modelOptions,
    ]);

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <ControlledList<IOrderPaymentModel>
                uniqueListName={`${props.formName}@${props.fieldName}--custom-list`}
                controllerName={props.formName}
                records={fields as any}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                listContextValues={{getCustomColumnComponent}}
                bordered={true}
                className="mb-1"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function OrderPaymentsField<Model extends IOrderModel = IOrderModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
